import type { AlpineComponent } from 'alpinejs'
import Player from '@vimeo/player'

type CourseVideoArgs = {
  subjectId: number
  videos: Video[]
}

type Video = {
  id: number
  name: string
  duration: number
  time: string
  imageUrl: string
  languageId: number
  userWatchTime: number
  userProgress: number
  lastSavedUserWatchTime: number
}

type CourseVideoComponent = CourseVideoArgs & {
  selectedVideo: Video
  liveCourse: boolean
  triggerOpenTestHandler: () => void
  selectVideo: (video: Video) => Promise<void>
  saveState: () => Promise<void>
  readonly isCompleted: boolean
}

export const courseVideo = (args: CourseVideoArgs): AlpineComponent<CourseVideoComponent> => {
  // non-reactive data needs to be outside of alpine component
  let player = null as unknown as Player

  return {
    ...args,
    selectedVideo: null as unknown as Video,
    liveCourse: window.location.pathname.includes('/live_course/'),

    init() {
      this.selectedVideo = this.videos.filter((v) => v.userProgress !== 100)[0] || this.videos[0]!

      for (const video of this.videos) {
        video.lastSavedUserWatchTime = video.userWatchTime
      }

      if (this.isCompleted) this.triggerOpenTestHandler()
      this.$watch('isCompleted', (completed) => completed && this.triggerOpenTestHandler())

      player = new Player('course_video', {
        id: this.selectedVideo.id,
        portrait: false,
        byline: false,
        controls: true,
        responsive: true,
        start_time: this.selectedVideo.lastSavedUserWatchTime,
      })

      // Update video states when watching video
      player.on('timeupdate', (data) => {
        this.selectedVideo.userWatchTime = data.seconds
        this.selectedVideo.userProgress = Math.round(data.percent * 100)
      })

      // Save progress automatically every 30 seconds only if watching
      setInterval(async () => {
        if (!(await player.getPaused())) {
          await this.saveState()
        }
      }, 30000)

      // Saving video status when video is started first time
      player.on('play', async () => {
        if (!this.selectedVideo.lastSavedUserWatchTime) {
          await this.saveState()
        }
      })

      // Saving video status when video is paused or ended
      player.on('pause', async () => await this.saveState())

      if (this.liveCourse) {
        // Disable seeking forward beyond the user's latest save point
        player.on('seeked', ({ seconds }) => {
          if (seconds > this.selectedVideo.lastSavedUserWatchTime) {
            player.setCurrentTime(this.selectedVideo.lastSavedUserWatchTime)
          }
        })

        window.addEventListener('pauseCourseVideo', async () => await player.pause())
      }
    },

    // Enables course test button if student is progressed enough
    triggerOpenTestHandler() {
      document.dispatchEvent(new CustomEvent('enableCourseTest'))
    },

    // Function for loading specific video from vimeo
    async selectVideo(video) {
      try {
        // Scrolling back to main video container when video selected from the list
        document.querySelector('main')?.scrollIntoView({ behavior: 'smooth' })

        if (video == this.selectedVideo) return

        await this.saveState()
        await player.loadVideo(video.id)

        this.selectedVideo = video

        // Make sure playTime is within an acceptable range, confirming the video duration from the Vimeo API first
        const duration = await player.getDuration()
        const playTime = Math.min(Math.max(video.lastSavedUserWatchTime, 0), duration)

        if (playTime) {
          await player.setCurrentTime(playTime)
        }
        if (playTime !== duration) {
          await player.play()
        }
      } catch (error: unknown) {
        if (error instanceof Error) {
          switch (error.name) {
            case 'TypeError':
              alert('Type error!')
              break
            case 'PasswordError':
              alert('Wrong password!')
              break
            case 'PrivacyError':
              alert('Private video.')
              break
            case 'RangeError':
              alert('RangeError.')
              break
            default:
              break
          }
        }
      }
    },

    // Save video progress
    async saveState() {
      try {
        const watchedSeconds = Math.floor(this.selectedVideo.userWatchTime)
        if (this.selectedVideo.lastSavedUserWatchTime && watchedSeconds <= this.selectedVideo.lastSavedUserWatchTime)
          return

        const videoId = await player.getVideoId()

        const resultData = {
          video_progress: {
            language_id: this.selectedVideo.languageId,
            subject_id: this.subjectId,
            video_id: videoId,
            watch_time: watchedSeconds,
          },
        }

        const csrfToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement).content
        const headers = { 'X-CSRF-Token': csrfToken, 'Content-Type': 'application/json' }
        const res = await fetch('/save_vimeo_state', {
          method: 'POST',
          headers,
          body: JSON.stringify(resultData),
          redirect: 'manual',
        })

        if (res.ok) {
          this.selectedVideo.lastSavedUserWatchTime = watchedSeconds
        } else if (res.type === 'opaqueredirect') {
          // If the server responds with a redirect, the user is most likely logged out
          // Redirect to root path to prevent watching further without the results being saved
          window.location.assign('/')
        } else {
          const msg = await res.text()
          throw new Error(msg)
        }
      } catch (error: unknown) {
        await player.pause()
        if (error instanceof Error) {
          alert(error.message)
        }
      }
    },

    get isCompleted() {
      return this.videos.every((video) => video.lastSavedUserWatchTime / video.duration >= 0.98)
    },
  }
}
